import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/admin/DataTable";
import { SearchInput } from "@/components/admin/SearchInput";
import { StatusBadge } from "@/components/admin/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useParticipants } from "@/hooks/admin/participants";
import { AdminLayout } from "@/layouts/AdminLayout";
import type { UserRole, UserWithEnrollment } from "@/types";

const PAGE_SIZE = 20;

// Backend sorts by created_at/email/role; map TanStack column ids onto those.
const SORTABLE = new Set(["email", "created_at", "role"]);

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// The backend UserRole enum is lowercase ("admin"/"user"); filter values match it.
type RoleFilter = "ALL" | "admin" | "user";

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("ALL");
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);

  const sort = sorting[0];
  const participantsQuery = useParticipants({
    page,
    page_size: PAGE_SIZE,
    sort_by: (sort?.id ?? "created_at") as "email" | "created_at" | "role",
    sort_order: sort ? (sort.desc ? "desc" : "asc") : "desc",
    q: search || undefined,
    role: roleFilter === "ALL" ? undefined : (roleFilter as UserRole),
  });
  const data = participantsQuery.data ?? null;
  const error = participantsQuery.isError
    ? participantsQuery.error instanceof Error
      ? participantsQuery.error.message
      : "Failed to load participants"
    : null;

  const columns = useMemo<ColumnDef<UserWithEnrollment>[]>(
    () => [
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) =>
          row.original.email ? (
            <span className="font-medium text-gray-900">{row.original.email}</span>
          ) : (
            // Fall back to username only when the email hasn't synced from Clerk yet.
            <span className="font-medium text-gray-400">{row.original.username || "—"}</span>
          ),
      },
      {
        accessorKey: "clerk_user_id",
        header: "Clerk ID",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-gray-500">{row.original.clerk_user_id}</span>
        ),
      },
      {
        accessorKey: "role",
        header: "Role",
        cell: ({ row }) => {
          const isAdmin = String(row.original.role).toLowerCase() === "admin";
          return (
            <StatusBadge tone={isAdmin ? "info" : "neutral"}>
              {isAdmin ? "Admin" : "Participant"}
            </StatusBadge>
          );
        },
      },
      {
        accessorKey: "active_programme_name",
        header: "Active Cohort",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.active_programme_name ? (
            <span className="text-sm text-gray-900">{row.original.active_programme_name}</span>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          ),
      },
      {
        accessorKey: "created_at",
        header: "Joined",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">{formatDate(row.original.created_at)}</span>
        ),
      },
    ],
    []
  );

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const valid = next.filter((s) => SORTABLE.has(s.id));
      setPage(1);
      return valid.length ? valid : prev;
    });
  };

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <DataTable
          className="h-full"
          columns={columns}
          data={data?.items ?? []}
          isLoading={participantsQuery.isPending}
          error={error}
          onRetry={() => participantsQuery.refetch()}
          emptyMessage="No participants found"
          getRowId={(u) => u.id}
          onRowClick={(u) => navigate(`/admin/users/${u.id}`)}
          toolbar={
            <div className="flex items-center justify-between gap-3">
              <SearchInput
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setPage(1);
                }}
                placeholder="Search participants…"
              />
              <Select
                value={roleFilter}
                onValueChange={(v) => {
                  setRoleFilter(v as RoleFilter);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">Participant</SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
          manualPagination
          pageCount={data?.total_pages ?? 0}
          rowCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          manualSorting
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>
    </AdminLayout>
  );
}
